import React, { use, useEffect, useMemo } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { createPlatformStackNavigator } from "./createPlatformStackNavigator";
import CreateMissionJobTitle from "../screens/create-mission/CreateMissionJobTitle";
import CreateMissionSummary from "../screens/create-mission/CreateMissionSummary";
import CreateMissionSimulationSummary from "../screens/create-mission/CreateMissionSimulationSummary";
import CreateMissionManager, {
    CreateMissionScreensContext,
    getInitialMissionScreen,
} from "../contexts/CreateMissionManager";
import CreateMissionCCT from "../screens/create-mission/CreateMissionCCT";
import CreateMissionDuration from "../screens/create-mission/CreateMissionDuration";
import CreateMissionEarningType from "../screens/create-mission/CreateMissionEarningType";
import CreateMissionWorkingAddress from "../screens/create-mission/CreateMissionWorkingAddress";
import CreateMissionSalary from "../screens/create-mission/CreateMissionSalary";
import CreateMissionFees from "../screens/create-mission/CreateMissionFees";
import CreateMissionDeductions from "../screens/create-mission/CreateMissionDeductions";
import CreateMissionTimeSheet from "../screens/create-mission/CreateMissionTimeSheet";
import CreateMissionWorkingTimeMore from "../screens/create-mission/CreateMissionWorkingTimeMore";
import CreateMissionWorker from "../screens/create-mission/CreateMissionWorker";
import CreateMissionTestWorker from "../screens/create-mission/CreateMissionTestWorker";
import CreateContractPreSign from "../screens/create-mission/CreateContractPreSign";
import CreateMissionHotellerieRestaurantFees from "../screens/create-mission/CreateMissionHotellerieRestaurantFees";
import { Animated, Platform } from "react-native";
import ModalDialogFrame from "../components/ModalDialogFrame";
import { useTheme } from "../hooks/use-theme";
import { RootStackParamList } from "./RootNavigation";
import { useCurrentProfile } from "../hooks/use-current-profile";
import { ClientProfile } from "../__generated__/graphql";
import { useStepsHeader } from "../hooks/use-steps-header";
import DocuIllSvg from "../assets/images/docu_ill.svg";
import StepsManager from "../contexts/StepsManager";
import { STEPS_DURATION, StepsConfig } from "../ui_atoms/Steps";
import { useTracking } from "../hooks/tracking";
import { useI18n } from "../hooks/i18n-hooks";
import { isBefore, startOfYear } from "date-fns";

/****
 * This is where a new step in Mission Creation should be added.
 * After adding the step name here, you should:
 * - Add a property in CreateMissionStackParamList
 * - Associate the React component (screen) with the step name in StackStepInfo
 * - Add the screen in the `screensFlow` in CreateMissionManager. It will define when the screen will be displayed
 * - Implement the component in folder screens/create-mission
 */
export const missionScreens = [
    "CreateMissionWorker",
    "CreateMissionTestWorker",
    "CreateMissionWorkingAddress",
    "CreateMissionJobTitle",
    "CreateMissionCCT",
    "CreateMissionHotellerieRestaurantFees",
    "CreateMissionTimeSheet",
    "CreateMissionEarningType",
    "CreateMissionWorkingTimeMore",
    "CreateMissionDuration",
    "CreateMissionSalary",
    "CreateMissionFees",
    "CreateMissionDeductions",
    "CreateMissionSummary",
    "CreateContractPreSign",
    "CreateMissionSimulationSummary",
] as const;

export type MissionScreens = (typeof missionScreens)[number];

export type CreateMissionStackParamList = {
    CreateMissionJobTitle: undefined;
    CreateMissionSummary: undefined;
    CreateMissionSimulationSummary: undefined;
    CreateMissionWorker: undefined;
    CreateMissionTestWorker: undefined;
    CreateMissionCCT: undefined;
    CreateMissionHotellerieRestaurantFees: undefined;
    CreateMissionDuration: undefined;
    CreateMissionEarningType: undefined;
    CreateMissionWorkingAddress: undefined;
    CreateMissionWorkingTimeMore: undefined;
    CreateMissionSalary: undefined;
    CreateMissionFees: undefined;
    CreateMissionDeductions: undefined;
    CreateMissionTimeSheet: undefined;
    CreateContractPreSign: { contractId: string };
};

// On web, @react-navigation/native-stack does not animate transitions, so the wizard falls back
// to the JS stack (which animates on react-native-web) there. createPlatformStackNavigator picks
// the right engine per platform.
const Stack = createPlatformStackNavigator<CreateMissionStackParamList>();

const StackStepInfo = {
    CreateMissionJobTitle: CreateMissionJobTitle,
    CreateMissionWorker: CreateMissionWorker,
    CreateMissionTestWorker: CreateMissionTestWorker,
    CreateMissionWorkingAddress: CreateMissionWorkingAddress,
    CreateMissionCCT: CreateMissionCCT,
    CreateMissionHotellerieRestaurantFees: CreateMissionHotellerieRestaurantFees,
    CreateMissionDuration: CreateMissionDuration,
    CreateMissionEarningType: CreateMissionEarningType,
    CreateMissionWorkingTimeMore: CreateMissionWorkingTimeMore,
    CreateMissionSalary: CreateMissionSalary,
    CreateMissionFees: CreateMissionFees,
    CreateMissionDeductions: CreateMissionDeductions,
    CreateMissionTimeSheet: CreateMissionTimeSheet,
    CreateMissionSummary: CreateMissionSummary,
    CreateContractPreSign: CreateContractPreSign,
    CreateMissionSimulationSummary: CreateMissionSimulationSummary,
};

type CreateMissionStackProps = StackScreenProps<RootStackParamList, "CreateMissionStack">;

const CreateMissionStack: React.FC<CreateMissionStackProps> = ({ route }) => {
    // If missionName is the same as jobTitle (= mission.name is not customized), we remove it and it will be re-computed from activityType on createMission.
    const removeMissionName =
        route.params?.initialMission?.name ===
        route.params?.initialMission?.currentContract?.jobTitle;

    const {
        initialMission: {
            [removeMissionName ? "name" : "createdAt"]: _1,
            createdAt: _2,
            updatedAt: _3,
            clientInfo: _4,
            // Mission-level dates describe the source mission: keeping them would make the
            // draft render the duplicated mission's period instead of the contract being built.
            startDate: _5,
            endDate: _6,
            effectiveEndDate: _7,
            currentContract,
            documents,
            ...initialMissionRest
        } = {},
    } = route.params ?? {};

    const {
        startDate: _startDate,
        endDate: _endDate,
        effectiveEndDate: _effectiveEndDate,
        jobTitle: _jobTitle,
        missionInfo: _missionInfo,
        ...currentContractWithoutDates
    } = currentContract ?? {};
    // Backdating is allowed down to the first day of the current year
    const minStartDate = startOfYear(new Date());
    const startDateIsThisYear =
        !!currentContract?.startDate &&
        !isBefore(new Date(currentContract.startDate), minStartDate);
    const endDateIsThisYear =
        !!currentContract?.endDate && !isBefore(new Date(currentContract.endDate), minStartDate);
    const resolvedCurrentContract = currentContract
        ? {
              ...currentContractWithoutDates,
              ...(startDateIsThisYear && { startDate: currentContract.startDate }),
              ...(endDateIsThisYear && {
                  endDate: currentContract.endDate,
                  effectiveEndDate: currentContract.endDate,
              }),
          }
        : undefined;

    const initialMission = {
        ...initialMissionRest,
        ...(documents
            ? {
                  documents: documents.map((doc) => {
                      if (!doc) return doc;
                      const { id: _docId, ...rest } = doc;
                      return rest;
                  }),
              }
            : {}),
        ...(resolvedCurrentContract ? { currentContract: resolvedCurrentContract } : {}),
    };

    return (
        <CreateMissionManager
            initialParams={initialMission}
            createMissionMode={route.params?.createMissionMode}
        >
            <StepsManager>
                <CreateMissionStackContent />
            </StepsManager>
        </CreateMissionManager>
    );
};

const CreateMissionStackContent = () => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const currentProfile = useCurrentProfile<ClientProfile>();
    const { trackEvent } = useTracking();
    const initialRouteName = getInitialMissionScreen(currentProfile) as MissionScreens;

    useEffect(() => {
        void trackEvent("mission_creation_started");
    }, [trackEvent]);

    const { closeWizard, currentStepIndex, totalSteps, stepsInfo } = use(
        CreateMissionScreensContext,
    );

    const visibleStepsInfo = useMemo(() => stepsInfo.filter((e) => e.isVisible), [stepsInfo]);

    const stepsConfig = useMemo<StepsConfig>(
        () => ({
            seenColor: colors.primary,
            currentColor: colors.accentLighter,
            currentStep: {
                component: CurrentStep,
                size: CURRENT_STEP_SIZE,
            },
        }),
        [colors.primary, colors.accentLighter],
    );

    const { headerComponent, screenListeners } = useStepsHeader<keyof CreateMissionStackParamList>({
        onClose: closeWizard,
        getCurrentIndex: currentStepIndex,
        getTotalSteps: () => totalSteps,
        headerHidden: visibleStepsInfo.map((e) => e.noHeader || false),
        stepsConfig,
        // The wizard is presented as a native modal (iOS page sheet), which already starts below the
        // status bar — don't add the device top inset again.
        disableSafeAreaTop: Platform.OS === "ios",
    });

    const title = i18n.t("screens.user.missions.create_new_mission");

    return (
        <ModalDialogFrame>
            {headerComponent}
            <Stack.Navigator
                initialRouteName={initialRouteName}
                screenListeners={screenListeners}
                // Keep inactive screens attached on web (JS-stack default detaches them with
                // display:none, which hides the outgoing/closing card and prevents the fade). Paired
                // with a react-native-screens patch that preserves the card fill style when
                // detached-rendering is disabled. Ignored by native-stack. cardStyleInterpolator
                // then cross-fades on web.
                detachInactiveScreens={false}
                screenOptions={{
                    headerShown: false,
                    // `animation: "fade"` is valid on both engines. On web (JS stack) it turns the
                    // animation ON (without it the default is "none": the tween is skipped AND the
                    // outgoing route is unmounted instantly). On native (native-stack) it drives the
                    // native cross-fade.
                    animation: "fade",
                }}
                nativeScreenOptions={{
                    // Native transition: starts immediately and is not gated on the JS render of the
                    // incoming screen (unlike @react-navigation/stack, which defers the entry
                    // animation behind a setTimeout). Keeps the card in sync with the steps header;
                    // matched to STEPS_DURATION for the same duration.
                    animationDuration: STEPS_DURATION,
                }}
                webScreenOptions={{
                    detachPreviousScreen: false,
                    // The cardStyleInterpolator / transitionSpec override the web transition preset's
                    // own. The interpolator is the canonical symmetric cross-fade:
                    // opacity = f(current + next) so the entering card fades in while the
                    // outgoing/closing one fades out.
                    cardStyleInterpolator: ({ current, next }) => {
                        const opacity = Animated.add(
                            current.progress,
                            next ? next.progress : 0,
                        ).interpolate({
                            inputRange: [0, 1, 2],
                            outputRange: [0, 1, 0],
                        });
                        return { cardStyle: { opacity } };
                    },
                    transitionSpec: {
                        open: { animation: "timing", config: { duration: STEPS_DURATION } },
                        close: { animation: "timing", config: { duration: STEPS_DURATION } },
                    },
                }}
            >
                {missionScreens.map((c) => (
                    <Stack.Screen
                        key={c}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        name={c}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={StackStepInfo[c]}
                        options={{ title }}
                    />
                ))}
            </Stack.Navigator>
        </ModalDialogFrame>
    );
};

const CURRENT_STEP_SIZE = 44;

const CurrentStep = <DocuIllSvg width="100%" height="100%" />;

export default CreateMissionStack;
